import { Component, HostBinding, OnInit, OnDestroy } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { NzNotificationService } from 'ng-zorro-antd/notification';
import { AuthClient, ResetPasswordRequest } from '../../../clients/client.generated';
import { getRequiredFieldMessage, getInvalidFieldMessage } from '../../shared/constants';
import { pageAnimation } from '@app/core/animations/element.animations';

@Component({
    selector: 'app-reset-password',
    templateUrl: './reset-password.component.html',
    styleUrls: ['./reset-password.component.scss'],
    animations: [pageAnimation]
})
export class ResetPasswordComponent implements OnInit, OnDestroy {

    @HostBinding('@pageAnimation') animate = true;
    form: UntypedFormGroup;
    readonly = true;
    submitted = false;
    unknownErrorMessages: string[] = [];
    isAuthenticating = false;
    forgotPassword = false;
    private onDestroy$ = new Subject<void>();

    constructor(
        private fb: UntypedFormBuilder,
        private authClient: AuthClient,
        private router: Router,
        private notification: NzNotificationService) { }

    /**
   * Marks all controls in a form group as touched
   * @param formGroup - The form group to touch
   */
    private markFormGroupDirtyTouched(formGroup: UntypedFormGroup) {
        for (const key of Object.keys(formGroup.controls)) {
            formGroup.controls[key].markAsDirty();
            formGroup.controls[key].updateValueAndValidity();
        }
    }

    ngOnInit() {
        this.forgotPassword = false;
        this.createForm();
    }

    ngOnDestroy(): void {
        this.onDestroy$.next();
        this.onDestroy$.complete();
    }

    createForm() {
        this.form = this.fb.group({
            email: [null, [Validators.email, Validators.required]]
        });
    }

    onLogin() {
        this.router.navigate(['account', 'login']);
    }

    onForgotPassword() {
        const control = this.form.get('email');
        const email = `${control.value}`.trim();

        if (!this.form.valid) {
            this.markFormGroupDirtyTouched(this.form);
            return;
        }

        this.clearErrors();
        this.isAuthenticating = true;

        const request = new ResetPasswordRequest({
            email: email
        });

        this.authClient.resetPassword(request).pipe(
            takeUntil(this.onDestroy$)
        ).subscribe({
            next: (response) => {
                this.isAuthenticating = false;
                this.notification.success('Email Sent', response.message
                    || 'Password reset instructions have been sent to your email address.');
                this.router.navigate(['account', 'login']);
            },
            error: (error) => {
                this.isAuthenticating = false;
                this.handleError(error);
            }
        });
    }

    private handleError(error: any): void {
        if (error.status === 404) {
            this.unknownErrorMessages = ['No account found with that email address.'];
        } else if (error.status === 400) {
            this.unknownErrorMessages = ['Please enter a valid email address.'];
        } else {
            this.unknownErrorMessages = ['An error occurred while sending the reset email. Please try again.'];
        }
    }

    clearErrors() {
        this.unknownErrorMessages = [];
    }

    getErrorMessage(formControlName: string) {
        const formControl = this.form.get(formControlName);
        if (formControl.hasError('required')) {
            return getRequiredFieldMessage(formControlName);
        } else if (formControl.hasError(formControlName)) {
            return getInvalidFieldMessage(formControlName);
        }
    }
}

